import { useEffect, useState } from "react";

const STORAGE_KEY = "admin_property_draft";
const MAX_IMAGES = 10;

/* ================= AMENITIES ================= */
const AMENITIES_LIST = [
  "24x7 Security",
  "CCTV Surveillance",
  "Clubhouse",
  "Swimming Pool",
  "Gymnasium",
  "Jogging Track",
  "Children’s Play Area",
  "Landscaped Gardens",
  "Power Backup",
  "Covered Parking",
  "EV Charging Point",
  "Multipurpose Hall",
  "Indoor Games Room",
  "Yoga / Meditation Area",
  "Senior Citizen Sit-out",
  "Co-working Space",
];

/* ================= DEFAULT ================= */
const defaultProperty = {
  title: "",
  location: "",
  startingPrice: "",
  pricePerSqft: "",
  bhkType: "",
  phone: "",
  whatsapp: "",
  amenities: [],
  superBuiltUpArea: "",
  carpetArea: "",
  configuration: "",
  facing: "",
  floor: "",
  ownership: "",
  parking: "",
  possession: "",
  developerName: "",
  projectReraNumber: "",
  reraAuthority: "",
  projectStatus: "",
  possessionDate: "",
  projectWebsite: "",
  nearby: [],
  moreNearby: "",
  description: "",
  images: [],
  createdAt: Date.now(),
};

const AdminPropertyForm = () => {
  const [property, setProperty] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : defaultProperty;
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(property));
  }, [property]);

  const clearDraft = () => {
    localStorage.removeItem(STORAGE_KEY);
    setProperty({ ...defaultProperty, createdAt: Date.now() });
  };

  const validateProperty = () => {
    if (!property.title) return "Project title is required";
    if (!property.location) return "Location is required";
    if (!property.startingPrice) return "Starting price is required";
    if (!property.superBuiltUpArea) return "Super built-up area is required";
    if (!property.phone) return "Phone number is required";
    if (!property.images.length) return "At least one image is required";
    return null;
  };

  const saveToMongo = async () => {
    const error = validateProperty();
    if (error) return alert(error);

    try {
      const res = await fetch(
        "https://thakkar-backend.vercel.app/api/property",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(property),
        }
      );

      const data = await res.json();
      if (data.success) {
        alert("✅ Property saved");
        clearDraft();
      } else {
        alert("❌ Failed to save");
      }
    } catch {
      alert("❌ Server error");
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 px-3 sm:px-4 py-6 sm:py-10">
      <div className="max-w-6xl mx-auto space-y-6 sm:space-y-8">

        {/* HEADER */}
        <div className="flex flex-col sm:flex-row sm:justify-between gap-3">
          <h1 className="text-xl sm:text-3xl font-bold">
            🏠 Property Admin Panel
          </h1>
          <button onClick={clearDraft} className="text-red-600 text-sm">
            Clear Draft
          </button>
        </div>

        {/* HERO */}
        <Section title="Hero & Pricing">
          <Grid>
            <Input placeholder="Project Title" value={property.title}
              onChange={(e) => setProperty({ ...property, title: e.target.value })} />
            <Input placeholder="Location" value={property.location}
              onChange={(e) => setProperty({ ...property, location: e.target.value })} />
            <Input placeholder="Starting Price" value={property.startingPrice}
              onChange={(e) => setProperty({ ...property, startingPrice: e.target.value })} />
            <Input placeholder="Price per Sq.ft" value={property.pricePerSqft}
              onChange={(e) => setProperty({ ...property, pricePerSqft: e.target.value })} />
            <Input placeholder="BHK Type" value={property.bhkType}
              onChange={(e) => setProperty({ ...property, bhkType: e.target.value })} />
            <Input placeholder="Super Built-up Area" value={property.superBuiltUpArea}
              onChange={(e) => setProperty({ ...property, superBuiltUpArea: e.target.value })} />
          </Grid>
        </Section>

        {/* CONTACT */}
        <Section title="Contact Details">
          <Grid>
            <Input placeholder="Phone" value={property.phone}
              onChange={(e) => setProperty({ ...property, phone: e.target.value })} />
            <Input placeholder="WhatsApp" value={property.whatsapp}
              onChange={(e) => setProperty({ ...property, whatsapp: e.target.value })} />
          </Grid>
        </Section>

        {/* PROJECT COMPLIANCE */}
        <Section title="Project Compliance (RERA)">
          <Grid>
            <Input
              placeholder="Developer Name"
              value={property.developerName}
              onChange={(e) => setProperty({ ...property, developerName: e.target.value })}
            />
            <Input
              placeholder="Project RERA Number"
              value={property.projectReraNumber}
              onChange={(e) => setProperty({ ...property, projectReraNumber: e.target.value })}
            />
            <Input
              placeholder="RERA Authority"
              value={property.reraAuthority}
              onChange={(e) => setProperty({ ...property, reraAuthority: e.target.value })}
            />
            <Input
              placeholder="Project Status"
              value={property.projectStatus}
              onChange={(e) => setProperty({ ...property, projectStatus: e.target.value })}
            />
            <Input
              placeholder="Possession Date"
              value={property.possessionDate}
              onChange={(e) => setProperty({ ...property, possessionDate: e.target.value })}
            />
            <Input
              placeholder="Official Project Website (https://...)"
              value={property.projectWebsite}
              onChange={(e) => setProperty({ ...property, projectWebsite: e.target.value })}
            />
          </Grid>
        </Section>

        {/* AMENITIES */}
        <Section title="Amenities">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
            {AMENITIES_LIST.map((a) => (
              <label key={a} className="flex gap-3 border p-3 rounded-xl text-sm">
                <input
                  type="checkbox"
                  checked={property.amenities.includes(a)}
                  onChange={(e) =>
                    setProperty({
                      ...property,
                      amenities: e.target.checked
                        ? [...property.amenities, a]
                        : property.amenities.filter(x => x !== a),
                    })
                  }
                />
                {a}
              </label>
            ))}
          </div>
        </Section>

        {/* IMAGES */}
        <Section title="Property Images">
          <input
            type="file"
            accept="image/*"
            multiple
            className="block w-full text-sm"
            onChange={(e) => {
              const files = Array.from(e.target.files).slice(
                0,
                MAX_IMAGES - property.images.length
              );

              files.forEach((file) => {
                if (file.size > 2 * 1024 * 1024) {
                  alert("Image must be under 2MB");
                  return;
                }
                const reader = new FileReader();
                reader.onloadend = () => {
                  setProperty(prev => ({
                    ...prev,
                    images: [...prev.images, reader.result],
                  }));
                };
                reader.readAsDataURL(file);
              });
            }}
          />

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mt-4">
            {property.images.map((img, i) => (
              <div key={i} className="relative">
                <img
                  src={img}
                  alt={`Property preview ${i + 1}`}
                  className="h-28 w-full object-cover rounded-xl"
                />
                <button
                  onClick={() =>
                    setProperty({
                      ...property,
                      images: property.images.filter((_, idx) => idx !== i),
                    })
                  }
                  className="absolute top-2 right-2 bg-red-600 text-white w-6 h-6 rounded-full text-xs"
                >
                  ✕
                </button>
              </div>
            ))}

          </div>
        </Section>

        {/* SAVE */}
        <div className="sticky bottom-0 bg-slate-100 py-3">
          <button
            onClick={saveToMongo}
            className="w-full bg-blue-600 text-white py-4 rounded-xl font-semibold"
          >
            💾 Save Property
          </button>
        </div>

      </div>
    </div>
  );
};

/* ================= UI HELPERS ================= */
const Section = ({ title, children }) => (
  <div className="bg-white p-4 sm:p-6 rounded-2xl shadow space-y-4">
    <h2 className="text-lg sm:text-xl font-semibold">{title}</h2>
    {children}
  </div>
);

const Grid = ({ children }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>
);

const Input = (props) => (
  <input {...props} className="w-full p-3 border rounded-xl text-sm sm:text-base" />
);

export default AdminPropertyForm;
